import {FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {DigitalSigningStatusView} from "../digitalSigning.model";
import {DigitalSigningStatus} from "../public/digitalSigning.types";

export const DIGITAL_SIGNING_POLL_MS = 3000;
export const DIGITAL_SIGNING_BUTTON_SX = {minHeight: 40};

export const digitalSigningUrl = (origin: string, token: string): string => (
    `${origin}/public/confirm#t=${encodeURIComponent(token)}`
);

export const copyText = async (text: string): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        try {
            return document.execCommand('copy');
        } catch {
            return false;
        } finally {
            document.body.removeChild(field);
        }
    }
};

export const remainingMs = (expiresAt: Date | string | undefined, nowMs: number): number | undefined => {
    const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : NaN;
    return Number.isNaN(expiresAtMs) ? undefined : expiresAtMs - nowMs;
};

export const countdownText = (remaining: number): string => {
    const totalSeconds = Number.isFinite(remaining) ? Math.max(0, Math.ceil(remaining / 1000)) : 0;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const isLiveDigitalSigning = (status?: DigitalSigningStatus): boolean => (
    status === 'PENDING' || status === 'OPENED'
);

export const isSignedDigitalSigning = (status?: DigitalSigningStatus): boolean => (
    status === 'SIGNED' || status === 'CONFIRMED'
);

export const digitalSigningPollInterval = (
    status: DigitalSigningStatus | undefined,
    dialogOpen: boolean,
    pageVisible: boolean
): number | false => (
    dialogOpen && pageVisible && isLiveDigitalSigning(status) ? DIGITAL_SIGNING_POLL_MS : false
);

export type DigitalSigningLinesIssue = 'NO_LINES' | 'NO_PRICE';

export const digitalSigningLinesIssue = (items: FolioItem[]): DigitalSigningLinesIssue | undefined => {
    const lines = items.filter(item => !item.archived);

    if (!lines.length) return 'NO_LINES';
    if (lines.some(item => !item.inclusive && item.price?.amountCents == null)) return 'NO_PRICE';
    return undefined;
};

export type DigitalSigningFlowState =
    | {kind: 'HIDDEN'}
    | {kind: 'DRAFT', previous?: DigitalSigningStatus, blockedBy?: DigitalSigningLinesIssue}
    | {kind: 'LIVE'}
    | {kind: 'LIVE_WITHOUT_TOKEN'}
    | {kind: 'SIGNED'}
    | {kind: 'CONFIRMED', incomplete?: true};

export const digitalSigningFlowState = ({latest, tokenKnown, folioStatus, linesIssue}: {
    latest?: DigitalSigningStatusView
    tokenKnown: boolean
    folioStatus?: FolioStatus
    linesIssue?: DigitalSigningLinesIssue
}): DigitalSigningFlowState => {
    const status = latest?.status;

    if (status === 'CONFIRMED') {
        return latest?.confirmIncomplete && folioStatus === FolioStatus.DRAFT ? {kind: 'CONFIRMED', incomplete: true} : {kind: 'CONFIRMED'};
    }
    if (folioStatus !== FolioStatus.DRAFT) return {kind: 'HIDDEN'};

    switch (status) {
        case 'PENDING':
        case 'OPENED':
            return tokenKnown ? {kind: 'LIVE'} : {kind: 'LIVE_WITHOUT_TOKEN'};
        case 'SIGNED':
            return {kind: 'SIGNED'};
        default:
            return {kind: 'DRAFT', previous: status, blockedBy: linesIssue};
    }
};
